// Export dialog — modal that lets the user pick a date range and download
// either a full PDF report or a tax-friendly XLSX/CSV of closed positions.
//
// The PDF generator (src/lib/export-pdf.ts) draws everything natively in
// jsPDF — no offscreen DOM mounting, no html2canvas. We just hand it the
// filtered data and the meta block.

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, FileSpreadsheet, FileText, Loader2, X } from 'lucide-react';
import { buildTaxRows, buildCsvBlob, buildXlsxBlob, taxFilename } from '@/lib/export-tax';
import { generateAccountPdf, pdfFilename } from '@/lib/export-pdf';
import { veritas, fetchAllPages } from '@/lib/supabase';
import type {
  AccountSnapshot, ClosedPosition, Tag,
} from '@/types/veritas';
import type { TradeRow } from '@/lib/metrics';

type Props = {
  open: boolean;
  onClose: () => void;
  accountLogin: number;
  ownerEmail: string | null;
  broker: string | null;
  eaVersion: string | null;
  // P2: the page no longer pre-loads all raw snapshots. The PDF needs the
  // equity curve for the chosen range, so the dialog lazy-fetches snapshots
  // on PDF generation, scoped to this credential (null = all credentials).
  credentialId: string | null;
  positionTags: Map<number, Set<string>>;
  tags: Tag[];
};

// Compute sensible defaults from the data: min and max of closed_at dates.
// If no positions yet, fall back to today.
function defaultRange(positions: ClosedPosition[]): { start: string; end: string } {
  const today = new Date().toISOString().slice(0, 10);
  const dates = positions
    .map((p) => p.closed_at)
    .filter((s): s is string => !!s)
    .map((s) => s.slice(0, 10))
    .sort();
  if (dates.length === 0) return { start: today, end: today };
  return { start: dates[0], end: dates[dates.length - 1] };
}

type Preset = 'all' | 'ytd' | 'last_year' | 'last_30';

export function ExportDialog(props: Props) {
  const { open, onClose, accountLogin, credentialId } = props;
  // Option 2: the page no longer holds full trade/position history. The export
  // lazy-fetches the full set (credential-scoped) when the dialog opens — off
  // the page-load critical path. Buttons stay disabled (nothingToExport) until
  // the data arrives.
  const [trades, setTrades] = useState<TradeRow[]>([]);
  const [positions, setPositions] = useState<ClosedPosition[]>([]);
  const initial = useMemo(() => defaultRange(positions), [positions]);
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);
  const [busy, setBusy] = useState<'pdf' | 'xlsx' | 'csv' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    (async () => {
      const tradesQ = fetchAllPages<TradeRow>((from, to) => {
        let q = veritas
          .from('trade_fills')
          .select('deal_ticket,snapshot_ts,symbol,deal_type,deal_reason,magic,volume,profit,credential_id')
          .eq('account_login', accountLogin)
          .order('snapshot_ts', { ascending: true })
          .order('deal_ticket', { ascending: true })
          .range(from, to);
        if (credentialId) q = q.eq('credential_id', credentialId);
        return q;
      });
      const posQ = fetchAllPages<ClosedPosition>((from, to) => {
        let q = veritas
          .from('closed_positions')
          .select('*')
          .eq('account_login', accountLogin)
          .order('closed_at', { ascending: true })
          .order('position_ticket', { ascending: true })
          .range(from, to);
        if (credentialId) q = q.eq('credential_id', credentialId);
        return q;
      });
      const [tRes, pRes] = await Promise.all([tradesQ, posQ]);
      if (cancelled) return;
      if (tRes.error) setError(tRes.error.message);
      if (pRes.error) setError(pRes.error.message);
      setTrades((tRes.data ?? []) as TradeRow[]);
      setPositions((pRes.data ?? []) as ClosedPosition[]);
    })();
    return () => { cancelled = true; };
  }, [open, accountLogin, credentialId]);

  // Keep the date range tracking the freshly-loaded data.
  useEffect(() => {
    if (open) { setStart(initial.start); setEnd(initial.end); }
  }, [open, initial.start, initial.end]);

  if (!open) return null;

  // ── Filter helpers ──────────────────────────────────────────────────
  // Inclusive on both ends, in UTC. Snapshots use snapshot_ts; positions
  // and trades use closed_at / snapshot_ts respectively.
  const startMs = Date.parse(`${start}T00:00:00Z`);
  const endMs = Date.parse(`${end}T23:59:59.999Z`);
  const inRange = (iso: string | null | undefined) => {
    if (!iso) return false;
    const t = Date.parse(iso);
    return Number.isFinite(t) && t >= startMs && t <= endMs;
  };

  const filteredPositions = positions.filter((p) => inRange(p.closed_at));
  const filteredTrades    = trades.filter((t) => inRange(t.snapshot_ts));

  const rangeValid = startMs <= endMs;
  const nothingToExport = filteredPositions.length === 0 && filteredTrades.length === 0;

  // ── Preset handlers ─────────────────────────────────────────────────
  const applyPreset = (preset: Preset) => {
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    if (preset === 'all') {
      setStart(initial.start); setEnd(initial.end);
    } else if (preset === 'ytd') {
      setStart(`${today.getUTCFullYear()}-01-01`); setEnd(todayStr);
    } else if (preset === 'last_year') {
      const y = today.getUTCFullYear() - 1;
      setStart(`${y}-01-01`); setEnd(`${y}-12-31`);
    } else if (preset === 'last_30') {
      const back = new Date(today.getTime() - 30 * 86_400_000);
      setStart(back.toISOString().slice(0, 10)); setEnd(todayStr);
    }
  };

  // ── Download triggers ───────────────────────────────────────────────
  const trigger = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const handleCsv = () => {
    setBusy('csv'); setError(null);
    try {
      const rows = buildTaxRows(filteredPositions, { startDate: start, endDate: end });
      const blob = buildCsvBlob(rows);
      trigger(blob, taxFilename(accountLogin, start, end, 'csv'));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const handleXlsx = async () => {
    setBusy('xlsx'); setError(null);
    try {
      const rows = buildTaxRows(filteredPositions, { startDate: start, endDate: end });
      const blob = await buildXlsxBlob(rows, {
        accountLogin,
        rangeStart: start,
        rangeEnd: end,
      });
      trigger(blob, taxFilename(accountLogin, start, end, 'xlsx'));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const handlePdf = async () => {
    setBusy('pdf'); setError(null);
    try {
      // P2: lazy-fetch the equity-curve snapshots for the selected range only
      // when a PDF is actually generated (the page no longer pre-loads them).
      // Note: raw snapshots are retained ~30 days, so curves for older ranges
      // are sparse — same as before P2 (retention already bounded the table).
      const snapRes = await fetchAllPages<AccountSnapshot>((from, to) => {
        let q = veritas
          .from('account_snapshots')
          .select('*')
          .eq('account_login', accountLogin)
          .gte('snapshot_ts', `${start}T00:00:00Z`)
          .lte('snapshot_ts', `${end}T23:59:59.999Z`)
          .order('snapshot_ts', { ascending: true })
          .order('id', { ascending: true }) // unique tiebreaker — see fetchAllPages contract
          .range(from, to);
        if (credentialId) q = q.eq('credential_id', credentialId);
        return q;
      });
      if (snapRes.error) throw new Error(snapRes.error.message);

      const blob = await generateAccountPdf(
        {
          accountLogin,
          rangeStart: start,
          rangeEnd: end,
          ownerEmail: props.ownerEmail,
          broker: props.broker,
          eaVersion: props.eaVersion,
        },
        {
          snapshots: (snapRes.data ?? []) as AccountSnapshot[],
          trades: filteredTrades,
          positions: filteredPositions,
          positionTags: props.positionTags,
          tags: props.tags,
        },
      );
      trigger(blob, pdfFilename(accountLogin, start, end));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center"
      onClick={() => !busy && onClose()}
    >
      <div
        className="relative flex w-full flex-col rounded-t-xl border border-line bg-bg-1 shadow-2xl sm:max-w-[480px] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line-soft px-5 py-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-eyebrow text-text-dim">
              Export
            </div>
            <div className="num text-[14px] font-semibold text-text">
              Account {accountLogin}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={!!busy}
            className="rounded-md border border-line p-2 text-text-mid hover:bg-bg-2 hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {/* Date range */}
          <label className="mb-2 block text-[10px] font-bold uppercase tracking-eyebrow text-text-dim">
            Date range
          </label>
          <div className="mb-3 grid grid-cols-2 gap-3">
            <div>
              <div className="mb-1 text-[10px] text-text-dim">Start</div>
              <input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                disabled={!!busy}
                className="num w-full rounded-md border border-line bg-bg-2 px-3 py-2 text-[13px] text-text outline-none transition-colors focus:border-green disabled:opacity-50"
              />
            </div>
            <div>
              <div className="mb-1 text-[10px] text-text-dim">End</div>
              <input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                disabled={!!busy}
                className="num w-full rounded-md border border-line bg-bg-2 px-3 py-2 text-[13px] text-text outline-none transition-colors focus:border-green disabled:opacity-50"
              />
            </div>
          </div>

          {/* Presets */}
          <div className="mb-4 flex flex-wrap gap-1.5">
            <PresetChip onClick={() => applyPreset('all')} disabled={!!busy}>All time</PresetChip>
            <PresetChip onClick={() => applyPreset('ytd')} disabled={!!busy}>This year</PresetChip>
            <PresetChip onClick={() => applyPreset('last_year')} disabled={!!busy}>Last year</PresetChip>
            <PresetChip onClick={() => applyPreset('last_30')} disabled={!!busy}>Last 30 days</PresetChip>
          </div>

          {/* Stats line */}
          <div className="mb-4 rounded-md border border-line bg-bg-2 px-3 py-2 text-[11px] text-text-mid">
            <span className="num">{filteredPositions.length}</span>{' '}
            closed positions
            <span className="px-2 text-text-faint">·</span>
            <span className="num">{filteredTrades.length}</span> deal events
            {!rangeValid && (
              <span className="ml-2 text-red">· Start must be on or before End</span>
            )}
          </div>

          {/* Action buttons */}
          <div className="grid gap-2">
            <ExportButton
              onClick={handlePdf}
              disabled={!!busy || !rangeValid || nothingToExport}
              loading={busy === 'pdf'}
              icon={<FileText size={14} />}
              label="Download PDF report"
              hint="Full report — vector charts, A4 landscape"
            />
            <ExportButton
              onClick={handleXlsx}
              disabled={!!busy || !rangeValid || nothingToExport}
              loading={busy === 'xlsx'}
              icon={<FileSpreadsheet size={14} />}
              label="Download tax XLSX"
              hint="Closed positions for accountants"
            />
            <ExportButton
              onClick={handleCsv}
              disabled={!!busy || !rangeValid || nothingToExport}
              loading={busy === 'csv'}
              icon={<Download size={14} />}
              label="Download tax CSV"
              hint="Plain CSV, universal import"
            />
          </div>

          {error && (
            <div className="mt-3 rounded-md border border-red/40 bg-red/10 px-3 py-2 text-[11px] text-red">
              {error}
            </div>
          )}
          {busy === 'pdf' && (
            <div className="mt-3 rounded-md border border-line bg-bg-2 px-3 py-2 text-[11px] text-text-mid">
              Drawing charts and packaging the PDF — usually 1–3 seconds…
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Helpers ────────────────────────────────────────────────────────────
function PresetChip({
  children, onClick, disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="rounded-md border border-line bg-bg-2 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-text-mid transition-colors hover:border-green hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}

function ExportButton({
  onClick, disabled, loading, icon, label, hint,
}: {
  onClick: () => void;
  disabled: boolean;
  loading: boolean;
  icon: React.ReactNode;
  label: string;
  hint: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-3 rounded-md border border-line bg-bg-2 px-3 py-2.5 text-left transition-colors hover:border-green hover:bg-bg-3 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-line bg-bg text-text-mid">
        {loading ? <Loader2 size={14} className="animate-spin" /> : icon}
      </span>
      <span className="flex-1">
        <span className="block text-[12px] font-semibold text-text">{label}</span>
        <span className="block text-[10.5px] text-text-dim">{hint}</span>
      </span>
    </button>
  );
}
